import React, { useState, useEffect, useRef, useCallback } from 'react';
import SourceSelector from '../components/SourceSelector';
import {
  Layout, List, Button, Input, Card, Tag, Space, Typography, Tooltip,
  Select, Modal, Spin, Badge, Divider, Empty, message, Drawer,
  Row, Col, Statistic, Alert, Steps, Avatar, Progress,
} from 'antd';
import {
  PlusOutlined, SendOutlined, DeleteOutlined, CopyOutlined,
  RobotOutlined, PlayCircleOutlined, BookOutlined, EyeOutlined,
  ThunderboltOutlined, CheckCircleOutlined, SyncOutlined,
  BulbOutlined, MessageOutlined, BarChartOutlined, DownloadOutlined, ReloadOutlined,
} from '@ant-design/icons';

const { Sider, Content } = Layout;
const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const API = '/api/v1';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`, 'Content-Type': 'application/json' });

// ── API helpers ────────────────────────────────────────────────────────────────
const api = {
  list: () => fetch(`${API}/presentations/`, { headers: authHeader() }).then(r => r.json()),
  create: (body) => fetch(`${API}/presentations/`, { method: 'POST', headers: authHeader(), body: JSON.stringify(body) }).then(r => r.json()),
  get: (id) => fetch(`${API}/presentations/${id}`, { headers: authHeader() }).then(r => r.json()),
  delete: (id) => fetch(`${API}/presentations/${id}`, { method: 'DELETE', headers: authHeader() }).then(r => r.json()),
  chat: (id, msg) => fetch(`${API}/presentations/${id}/chat`, { method: 'POST', headers: authHeader(), body: JSON.stringify({ message: msg }) }).then(r => r.json()),
  assignBot: (id, bot_id) => fetch(`${API}/presentations/${id}/bot`, { method: 'PATCH', headers: authHeader(), body: JSON.stringify({ bot_id }) }).then(r => r.json()),
  trainBot: (id) => fetch(`${API}/presentations/${id}/train-bot`, { method: 'POST', headers: authHeader() }).then(r => r.json()),
  previewKnowledge: (id) => fetch(`${API}/presentations/${id}/bot-knowledge`, { headers: authHeader() }).then(r => r.json()),
  clone: (id) => fetch(`${API}/presentations/${id}/clone`, { method: 'POST', headers: authHeader() }).then(r => r.json()),
  reparse: (id) => fetch(`${API}/presentations/${id}/reparse`, { method: 'POST', headers: authHeader() }).then(r => r.json()),
  exportHtmlUrl: (id) => `${API}/presentations/${id}/export-html`,
  exportPptxUrl: (id) => `${API}/presentations/${id}/export-pptx`,
  bots: () => fetch(`${API}/presentations/bots/list`, { headers: authHeader() }).then(r => r.json()),
};

// ── Slide type config ──────────────────────────────────────────────────────────
const SLIDE_TYPE = {
  cover: { color: '#722ed1', label: '封面' },
  agenda: { color: '#1890ff', label: '議程' },
  problem: { color: '#f5222d', label: '痛點' },
  solution: { color: '#52c41a', label: '解方' },
  product: { color: '#13c2c2', label: '產品' },
  comparison: { color: '#fa8c16', label: '比較' },
  case_study: { color: '#eb2f96', label: '案例' },
  pricing: { color: '#2f54eb', label: '報價' },
  cta: { color: '#fa541c', label: '行動' },
  end: { color: '#595959', label: '結語' },
};

const STATUS_CONFIG = {
  draft: { color: 'default', label: '草稿', icon: <MessageOutlined /> },
  ready: { color: 'processing', label: '簡報就緒', icon: <CheckCircleOutlined /> },
  bot_trained: { color: 'success', label: 'Bot 已學習', icon: <RobotOutlined /> },
};

// ── Slide Preview ──────────────────────────────────────────────────────────────
function SlidePreview({ slides_data }) {
  const [current, setCurrent] = useState(0);
  if (!slides_data?.slides?.length) return null;
  const slide = slides_data.slides[current];
  const conf = SLIDE_TYPE[slide.type] || { color: '#333', label: slide.type };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Text strong>{slides_data.title}</Text>
        <Tag color={conf.color}>{conf.label}</Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>{current + 1} / {slides_data.slides.length}</Text>
      </div>

      {/* Slide card */}
      <Card
        size="small"
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          border: 'none', borderRadius: 8, minHeight: 220, position: 'relative',
        }}
      >
        <div style={{ position: 'absolute', top: 12, right: 12 }}>
          <Tag color={conf.color} style={{ opacity: 0.9 }}>{conf.label}</Tag>
        </div>
        <Title level={4} style={{ color: '#fff', marginBottom: 12, marginTop: 4, fontSize: 16, paddingRight: 60 }}>
          {slide.title}
        </Title>
        {slide.bullets?.length > 0 && (
          <ul style={{ color: '#cbd5e1', margin: 0, paddingLeft: 16 }}>
            {slide.bullets.map((b, i) => (
              <li key={i} style={{ marginBottom: 4, fontSize: 13 }}>{b}</li>
            ))}
          </ul>
        )}
        {slide.visual_hint && (
          <div style={{ marginTop: 8, padding: '4px 8px', background: 'rgba(255,255,255,0.08)', borderRadius: 4 }}>
            <Text style={{ color: '#94a3b8', fontSize: 11 }}>💡 {slide.visual_hint}</Text>
          </div>
        )}
      </Card>

      {slide.notes && (
        <Alert
          message={<Text style={{ fontSize: 12 }}>🎤 {slide.notes}</Text>}
          type="info" showIcon={false} style={{ marginTop: 8, padding: '6px 10px' }}
        />
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        {slides_data.slides.map((s, i) => {
          const c = SLIDE_TYPE[s.type] || {};
          return (
            <Tooltip key={i} title={`${i + 1}. ${s.title}`}>
              <div
                onClick={() => setCurrent(i)}
                style={{
                  width: 28, height: 18, borderRadius: 3, cursor: 'pointer',
                  background: i === current ? (c.color || '#555') : '#e2e8f0',
                  border: i === current ? `2px solid ${c.color || '#555'}` : '1px solid #d1d5db',
                  transition: 'all .15s',
                }}
              />
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

// ── Sales Insights ─────────────────────────────────────────────────────────────
function SalesInsights({ slides_data }) {
  if (!slides_data) return null;
  return (
    <div style={{ marginTop: 16 }}>
      {slides_data.key_messages?.length > 0 && (
        <>
          <Text type="secondary" style={{ fontSize: 12 }}>核心銷售訊息</Text>
          <div style={{ marginTop: 4 }}>
            {slides_data.key_messages.map((m, i) => (
              <Tag key={i} color="blue" style={{ marginBottom: 4 }}>{m}</Tag>
            ))}
          </div>
        </>
      )}
      {slides_data.sales_techniques?.length > 0 && (
        <>
          <Divider style={{ margin: '10px 0' }} />
          <Text type="secondary" style={{ fontSize: 12 }}>銷售技巧</Text>
          <div style={{ marginTop: 4 }}>
            {slides_data.sales_techniques.map((t, i) => (
              <Tag key={i} icon={<BulbOutlined />} color="orange" style={{ marginBottom: 4 }}>{t}</Tag>
            ))}
          </div>
        </>
      )}
      {slides_data.objection_handling?.length > 0 && (
        <>
          <Divider style={{ margin: '10px 0' }} />
          <Text type="secondary" style={{ fontSize: 12 }}>異議處理 ({slides_data.objection_handling.length} 個)</Text>
          {slides_data.objection_handling.map((o, i) => (
            <Card key={i} size="small" style={{ marginTop: 6, background: '#fafafa' }}>
              <Text type="danger" style={{ fontSize: 12 }}>「{o.objection}」</Text>
              <br />
              <Text style={{ fontSize: 12 }}>→ {o.response}</Text>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function PresentationPage() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [training, setTraining] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bots, setBots] = useState([]);
  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', client_name: '', client_company: '', purpose: '', bot_id: null });
  const [knowledgeDrawer, setKnowledgeDrawer] = useState(false);
  const [knowledgeText, setKnowledgeText] = useState('');
  const [activeTab, setActiveTab] = useState('slides');
  const msgEndRef = useRef(null);

  const loadList = useCallback(async () => {
    const data = await api.list();
    setList(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => { loadList(); api.bots().then(d => setBots(Array.isArray(d) ? d : [])); }, [loadList]);

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [selected?.messages]);

  const selectPresentation = async (id) => {
    setLoading(true);
    try {
      const data = await api.get(id);
      setSelected(data);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    const data = await api.create(createForm);
    if (data?.id) {
      message.success('簡報已建立');
      setCreateModal(false);
      setCreateForm({ title: '', client_name: '', client_company: '', purpose: '', bot_id: null });
      await loadList();
      selectPresentation(data.id);
    }
  };

  const handleChat = async () => {
    if (!input.trim() || !selected) return;
    const msg = input.trim();
    setInput('');
    setSending(true);

    // Optimistic UI
    setSelected(prev => ({
      ...prev,
      messages: [...(prev.messages || []), { id: Date.now(), role: 'user', content: msg }],
    }));

    try {
      const res = await api.chat(selected.id, msg);
      const fresh = await api.get(selected.id);
      setSelected(fresh);
      if (res.slides_data) {
        message.success('簡報架構已產生！');
        loadList();
      }
    } finally {
      setSending(false);
    }
  };

  const handleAssignBot = async (bot_id) => {
    if (!selected) return;
    await api.assignBot(selected.id, bot_id);
    const fresh = await api.get(selected.id);
    setSelected(fresh);
    loadList();
    message.success(bot_id ? 'Bot 配對成功' : 'Bot 已解除配對');
  };

  const handleTrainBot = async () => {
    if (!selected?.bot_id) return message.warning('請先配對一個 AI Bot');
    if (!selected?.slides_data) return message.warning('請先產生簡報內容');
    setTraining(true);
    try {
      const res = await api.trainBot(selected.id);
      const fresh = await api.get(selected.id);
      setSelected(fresh);
      loadList();
      if (res.ok) {
        message.success(`Bot 學習成功！已傳授 ${res.chars?.toLocaleString() || '?'} 字元的銷售技巧`);
      } else {
        message.error(`傳授失敗：${res.error || res.reason || JSON.stringify(res)}`);
      }
    } finally {
      setTraining(false);
    }
  };

  const handlePreviewKnowledge = async () => {
    const res = await api.previewKnowledge(selected.id);
    setKnowledgeText(res.knowledge || '（無內容）');
    setKnowledgeDrawer(true);
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    Modal.confirm({
      title: '確定刪除此簡報？', okText: '刪除', cancelText: '取消', okButtonProps: { danger: true },
      onOk: async () => {
        await api.delete(id);
        if (selected?.id === id) setSelected(null);
        await loadList();
      },
    });
  };

  const handleReparse = async () => {
    if (!selected) return;
    const res = await api.reparse(selected.id);
    if (res.ok) {
      message.success(`解析成功！找到 ${res.slides} 張投影片：${res.title}`);
      const fresh = await api.get(selected.id);
      setSelected(fresh);
      loadList();
    } else {
      message.error(res.reason || '重新解析失敗');
    }
  };

  const downloadFile = (url, filename) => {
    const token = localStorage.getItem('spo_auth_token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  const handleDownload = () => {
    if (!selected?.slides_data) return message.warning('請先產生簡報內容');
    downloadFile(api.exportHtmlUrl(selected.id), `${selected.title || 'presentation'}.html`);
  };

  const handleDownloadPptx = () => {
    if (!selected?.slides_data) return message.warning('請先產生簡報內容');
    // Direct download from pre-built file server (port 18820)
    const host = window.location.hostname;
    const directUrl = `http://${host}:18820/Comboware_AI_Deck_FINAL.pptx`;
    // Check if this presentation has a pre-built file
    if (selected.title && selected.title.includes('Comboware')) {
      window.open(directUrl, '_blank');
      return;
    }
    // Fallback: generate via API
    message.loading({ content: '正在生成 PPTX...', key: 'pptx', duration: 30 });
    const token = localStorage.getItem('spo_auth_token');
    fetch(api.exportPptxUrl(selected.id), { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        message.success({ content: 'PPTX 下載完成！', key: 'pptx' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${selected.title || 'presentation'}.pptx`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => message.error({ content: 'PPTX 生成失敗', key: 'pptx' }));
  };

  const handleClone = async () => {
    const data = await api.clone(selected.id);
    if (data?.id) {
      message.success('已複製簡報');
      await loadList();
      selectPresentation(data.id);
    }
  };

  const STATUS = STATUS_CONFIG[selected?.status] || STATUS_CONFIG.draft;

  return (
    <Layout style={{ height: 'calc(100vh - 64px)', background: '#f0f2f5' }}>
      {/* ── Left: list ── */}
      <Sider width={280} style={{ background: '#fff', borderRight: '1px solid #f0f0f0', overflow: 'auto', padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Text strong style={{ fontSize: 15 }}>AI 簡報產生器</Text>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setCreateModal(true)}>新建</Button>
        </div>
        {list.length === 0 ? (
          <Empty description="尚無簡報" style={{ marginTop: 40 }} />
        ) : (
          <List
            dataSource={list}
            renderItem={item => {
              const s = STATUS_CONFIG[item.status] || STATUS_CONFIG.draft;
              const isActive = selected?.id === item.id;
              return (
                <div
                  onClick={() => selectPresentation(item.id)}
                  style={{
                    padding: '10px 12px', marginBottom: 6, borderRadius: 8, cursor: 'pointer',
                    background: isActive ? '#e6f4ff' : '#fafafa',
                    border: isActive ? '1px solid #1890ff' : '1px solid #f0f0f0',
                    transition: 'all .15s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Text ellipsis style={{ maxWidth: 160, fontSize: 13, fontWeight: isActive ? 600 : 400 }}>
                      {item.title}
                    </Text>
                    <Button danger type="text" size="small" icon={<DeleteOutlined />} onClick={e => handleDelete(item.id, e)} />
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                    <Tag color={s.color} style={{ fontSize: 11, padding: '0 5px' }}>{s.label}</Tag>
                    {item.slide_count > 0 && <Tag style={{ fontSize: 11, padding: '0 5px' }}>{item.slide_count} 張</Tag>}
                    {item.bot && <Tag color="purple" icon={<RobotOutlined />} style={{ fontSize: 11, padding: '0 5px' }}>{item.bot.name}</Tag>}
                  </div>
                  {item.client_company && <Text type="secondary" style={{ fontSize: 11 }}>{item.client_company}</Text>}
                </div>
              );
            }}
          />
        )}
        {/* ── Sales Decks Download ── */}
        <Divider style={{ margin: '16px 0 10px' }} />
        <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 10 }}>📥 銷售簡報下載</Text>
        {[
          {
            filename: 'JJ_Bangkok_Sales_Deck_EN_2026.pptx',
            label: '🏙️ JJ Bangkok Real Estate',
            desc: '曼谷房產 AI 平台銷售簡報 (EN)',
            color: '#0A1F3F',
            tag: 'NEW',
          },
          {
            filename: 'JJ_Bangkok_銷售簡報_繁中_2026.pptx',
            label: '🏙️ JJ 曼谷房產 AI 平台',
            desc: '銷售簡報（繁體中文版）',
            color: '#0A1F3F',
            tag: '繁中',
          },
          {
            filename: 'AlayaEss-Sales-Pitch-v1-20260507.pptx',
            label: '🤖 AlayaEss AI 員工派遣',
            desc: 'AI 業務 Bot 銷售簡報 (中文)',
            color: '#722ed1',
            tag: '',
          },
          {
            filename: 'AlayaEss-Sales-Pitch-en-v1-20260507.pptx',
            label: '🤖 AlayaEss AI Staffing',
            desc: 'AI Sales Bot Pitch Deck (EN)',
            color: '#722ed1',
            tag: '',
          },
          {
            filename: 'marcom-v01.pptx',
            label: '📊 marcom AI Platform',
            desc: '行銷平台完整銷售簡報',
            color: '#1890ff',
            tag: '',
          },
        ].map(deck => (
          <div key={deck.filename} style={{ marginBottom: 8, padding: '8px 10px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: deck.color, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {deck.label}
                  {deck.tag && <Tag color="red" style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>{deck.tag}</Tag>}
                </div>
                <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{deck.desc}</div>
              </div>
              <Tooltip title={`下載 ${deck.filename}`}>
                <Button
                  size="small" icon={<DownloadOutlined />} type="primary"
                  style={{ background: deck.color, border: 'none', marginLeft: 8, flexShrink: 0 }}
                  onClick={() => {
                    window.open(`/api/v1/public/decks/${encodeURIComponent(deck.filename)}`, '_blank');
                  }}
                />
              </Tooltip>
            </div>
          </div>
        ))}
      </Sider>

      {/* ── Right: workspace ── */}
      <Content style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {!selected ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Empty description="選擇一個簡報或點擊「新建」開始" />
          </div>
        ) : loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Spin size="large" />
          </div>
        ) : (
          <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {/* Chat panel */}
            <div style={{ flex: '0 0 420px', display: 'flex', flexDirection: 'column', background: '#fff', borderRight: '1px solid #f0f0f0' }}>
              {/* Header */}
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong style={{ fontSize: 14 }}>{selected.title}</Text>
                    <Tag icon={STATUS.icon} color={STATUS.color} style={{ marginLeft: 8, fontSize: 11 }}>{STATUS.label}</Tag>
                  </div>
                  <Space>
                    <Tooltip title="複製簡報"><Button icon={<CopyOutlined />} size="small" onClick={handleClone} /></Tooltip>
                    <Button danger icon={<DeleteOutlined />} size="small" onClick={() => handleDelete(selected.id)} />
                  </Space>
                </div>
                {/* Bot assignment row */}
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <RobotOutlined style={{ color: '#722ed1' }} />
                  <Select
                    size="small"
                    style={{ flex: 1 }}
                    placeholder="配對 AI Bot..."
                    value={selected.bot_id || undefined}
                    onChange={handleAssignBot}
                    allowClear
                    onClear={() => handleAssignBot(null)}
                  >
                    {bots.map(b => (
                      <Option key={b.id} value={b.id}>
                        {b.name}{b.alayaess_bot_name ? ` (${b.alayaess_bot_name})` : ' ⚠️'}
                      </Option>
                    ))}
                  </Select>
                  <Tooltip title={selected.bot?.alayaess_bot_name ? '' : 'Bot 尚未綁定 AlayaEss，傳授功能不可用'}>
                    <Button
                      size="small" type="primary"
                      icon={training ? <SyncOutlined spin /> : <ThunderboltOutlined />}
                      onClick={handleTrainBot}
                      loading={training}
                      disabled={!selected.bot_id || !selected.slides_data || !selected.bot?.alayaess_bot_name}
                      style={{ background: selected.status === 'bot_trained' ? '#52c41a' : undefined }}
                    >
                      傳授給 Bot
                    </Button>
                  </Tooltip>
                </div>
                {selected.bot_trained_at && (
                  <Alert
                    message={`✅ Bot「${selected.bot?.name}」已於 ${new Date(selected.bot_trained_at).toLocaleString('zh-TW')} 學習此簡報`}
                    type="success" showIcon={false} style={{ marginTop: 6, padding: '4px 8px', fontSize: 11 }}
                  />
                )}
                {/* Reference sources */}
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <SourceSelector
                    presentationId={selected.id}
                    initialSources={selected.reference_sources || []}
                    onSave={(sources) => setSelected(prev => ({ ...prev, reference_sources: sources }))}
                    trigger={
                      <Button size="small" icon={<span>📎</span>} style={{ fontSize: 12 }}>
                        參考來源
                        {selected.reference_sources?.length > 0 && (
                          <span style={{ marginLeft: 4, background: '#1890ff', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11 }}>
                            {selected.reference_sources.length}
                          </span>
                        )}
                      </Button>
                    }
                  />
                  {selected.reference_sources?.length > 0 && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {selected.reference_sources.map(s => s.name || s.url || s.path).join('、').slice(0, 60)}
                      {selected.reference_sources.map(s => s.name || s.url || '').join('').length > 60 ? '...' : ''}
                    </Text>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
                {(!selected.messages || selected.messages.length === 0) && (
                  <div style={{ textAlign: 'center', marginTop: 40 }}>
                    <BulbOutlined style={{ fontSize: 32, color: '#faad14', marginBottom: 12 }} />
                    <Paragraph type="secondary" style={{ fontSize: 13 }}>
                      描述你要製作的簡報：<br />
                      產品/服務、目標客戶、簡報目的，<br />
                      AI 將產生完整的投影片架構。
                    </Paragraph>
                    <div style={{ textAlign: 'left', background: '#f6ffed', padding: 12, borderRadius: 8, border: '1px solid #b7eb8f' }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>範例提示：</Text>
                      {[
                        '幫我做一份 ERP 系統的銷售簡報，給製造業 IT 主管看，主要強調降低庫存成本和生產效率',
                        '幫我製作資安產品簡報，客戶是金融業，強調合規性和零信任架構',
                      ].map((p, i) => (
                        <div key={i} onClick={() => setInput(p)} style={{ cursor: 'pointer', marginTop: 6, color: '#1890ff', fontSize: 12 }}>
                          → {p}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selected.messages?.map(m => (
                  <div key={m.id} style={{ marginBottom: 12, display: 'flex', flexDirection: m.role === 'user' ? 'row-reverse' : 'row', gap: 8 }}>
                    <Avatar size={28} style={{ background: m.role === 'user' ? '#1890ff' : '#722ed1', flexShrink: 0 }}>
                      {m.role === 'user' ? 'U' : 'AI'}
                    </Avatar>
                    <div
                      style={{
                        maxWidth: '78%', padding: '8px 12px', borderRadius: 10, fontSize: 13,
                        background: m.role === 'user' ? '#1890ff' : '#f5f5f5',
                        color: m.role === 'user' ? '#fff' : '#333',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}
                    >
                      {m.content.replace(/>>>SLIDES_READY[\s\S]*?SLIDES_READY<<</g, '✅ 簡報架構已產生，請查看右側預覽')}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <Avatar size={28} style={{ background: '#722ed1' }}>AI</Avatar>
                    <div style={{ padding: '8px 12px', background: '#f5f5f5', borderRadius: 10 }}>
                      <Spin size="small" />
                    </div>
                  </div>
                )}
                <div ref={msgEndRef} />
              </div>

              {/* Input */}
              <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <TextArea
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChat(); } }}
                    placeholder="描述你的簡報需求，或要求調整內容... (Enter 送出)"
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    style={{ fontSize: 13 }}
                  />
                  <Button
                    type="primary" icon={<SendOutlined />}
                    onClick={handleChat} loading={sending}
                    disabled={!input.trim()}
                    style={{ height: 'auto' }}
                  />
                </div>
              </div>
            </div>

            {/* Preview panel */}
            <div style={{ flex: 1, overflow: 'auto', padding: 20, background: '#f0f2f5' }}>
              {/* Tabs */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                {[
                  { key: 'slides', icon: <PlayCircleOutlined />, label: '投影片預覽' },
                  { key: 'insights', icon: <BarChartOutlined />, label: '銷售洞察' },
                ].map(t => (
                  <Button
                    key={t.key}
                    type={activeTab === t.key ? 'primary' : 'default'}
                    icon={t.icon}
                    onClick={() => setActiveTab(t.key)}
                    size="small"
                  >
                    {t.label}
                  </Button>
                ))}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  {!selected.slides_data && (
                    <Space>
                      <Tooltip title="從已有的 AI 回覆中重新解析投影片結構">
                        <Button size="small" icon={<ReloadOutlined />} onClick={handleReparse} type="dashed">
                          重新解析
                        </Button>
                      </Tooltip>
                      <Tooltip title="AI 回覆可能被截斷，點擊讓 AI 重新輸出完整簡報">
                        <Button size="small" icon={<SyncOutlined />} type="primary" danger
                          onClick={() => { setInput('請重新輸出完整的投影片 JSON 結構，確保包含完整的 SLIDES_READY 標記，不要省略任何投影片'); }}
                        >
                          要求重新輸出
                        </Button>
                      </Tooltip>
                    </Space>
                  )}
                  {selected.slides_data && (
                    <>
                      <Button size="small" icon={<DownloadOutlined />} onClick={handleDownloadPptx} type="primary" style={{ background: '#7c3aed', borderColor: '#7c3aed' }}>
                        下載 PPTX
                      </Button>
                      <Button size="small" icon={<DownloadOutlined />} onClick={handleDownload} type="default">
                        下載 HTML
                      </Button>
                      <Button size="small" icon={<EyeOutlined />} onClick={handlePreviewKnowledge}>
                        Bot 知識
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {!selected.slides_data ? (
                <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <PlayCircleOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                  <Title level={4} type="secondary">尚未產生簡報</Title>
                  <Paragraph type="secondary">在左側對話框輸入你的需求，AI 將產生完整的投影片架構</Paragraph>
                </Card>
              ) : activeTab === 'slides' ? (
                <Card>
                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={8}>
                      <Statistic title="投影片數量" value={selected.slides_data.slides?.length || 0} suffix="張" />
                    </Col>
                    <Col span={8}>
                      <Statistic title="異議處理" value={selected.slides_data.objection_handling?.length || 0} suffix="個" />
                    </Col>
                    <Col span={8}>
                      <Statistic title="銷售技巧" value={selected.slides_data.sales_techniques?.length || 0} suffix="項" />
                    </Col>
                  </Row>
                  <Divider style={{ margin: '12px 0' }} />
                  <SlidePreview slides_data={selected.slides_data} />
                </Card>
              ) : (
                <Card>
                  <SalesInsights slides_data={selected.slides_data} />
                </Card>
              )}

              {/* Bot training card */}
              {selected.slides_data && (
                <Card style={{ marginTop: 16, background: '#f9f0ff', border: '1px solid #d3adf7' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <RobotOutlined style={{ fontSize: 24, color: '#722ed1' }} />
                    <div style={{ flex: 1 }}>
                      <Text strong>Bot 學習功能</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {selected.bot
                          ? `已配對：${selected.bot.name}${selected.bot_trained_at ? '（已完成學習）' : '（尚未傳授）'}`
                          : '請在左側對話區配對一個 AI Bot'
                        }
                      </Text>
                    </div>
                    {selected.bot && !selected.bot_trained_at && (
                      <Button
                        type="primary"
                        icon={<ThunderboltOutlined />}
                        onClick={handleTrainBot}
                        loading={training}
                        disabled={!selected.bot?.alayaess_bot_name}
                      >
                        傳授銷售技巧給 Bot
                      </Button>
                    )}
                    {selected.bot_trained_at && (
                      <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                    )}
                  </div>
                  {selected.bot_push_result && (
                    <div style={{ marginTop: 8, fontSize: 11, color: '#999' }}>
                      上次推送：{JSON.stringify(selected.bot_push_result)}
                    </div>
                  )}
                </Card>
              )}
            </div>
          </div>
        )}
      </Content>

      {/* ── Create modal ── */}
      <Modal
        title="新建 AI 簡報"
        open={createModal}
        onOk={handleCreate}
        onCancel={() => setCreateModal(false)}
        okText="建立"
        cancelText="取消"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text type="secondary">簡報標題 *</Text>
            <Input
              value={createForm.title}
              onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
              placeholder="例：Comboware ERP 銷售簡報"
            />
          </div>
          <Row gutter={12}>
            <Col span={12}>
              <Text type="secondary">客戶姓名</Text>
              <Input value={createForm.client_name} onChange={e => setCreateForm(f => ({ ...f, client_name: e.target.value }))} placeholder="王大明" />
            </Col>
            <Col span={12}>
              <Text type="secondary">客戶公司</Text>
              <Input value={createForm.client_company} onChange={e => setCreateForm(f => ({ ...f, client_company: e.target.value }))} placeholder="ABC 科技" />
            </Col>
          </Row>
          <div>
            <Text type="secondary">簡報目的</Text>
            <TextArea
              value={createForm.purpose}
              onChange={e => setCreateForm(f => ({ ...f, purpose: e.target.value }))}
              placeholder="例：向製造業客戶介紹 ERP 系統，強調降低庫存成本..."
              rows={2}
            />
          </div>
          <div>
            <Text type="secondary">配對 AI Bot（可之後再設定）</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="選擇 AI Bot..."
              value={createForm.bot_id || undefined}
              onChange={v => setCreateForm(f => ({ ...f, bot_id: v }))}
              allowClear
            >
              {bots.map(b => (
                <Option key={b.id} value={b.id}>{b.name}</Option>
              ))}
            </Select>
          </div>
        </Space>
      </Modal>

      {/* ── Knowledge preview drawer ── */}
      <Drawer
        title="Bot 將學習的知識內容"
        open={knowledgeDrawer}
        onClose={() => setKnowledgeDrawer(false)}
        width={520}
        extra={<Button onClick={handleTrainBot} type="primary" loading={training} icon={<ThunderboltOutlined />}>立即傳授</Button>}
      >
        <Alert
          message="以下是 AI 將傳授給 Bot 的銷售知識，Bot 學習後能在對話中引用這些內容回答客戶問題。"
          type="info" style={{ marginBottom: 16 }}
        />
        <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: 12, borderRadius: 6, maxHeight: '70vh', overflow: 'auto' }}>
          {knowledgeText}
        </pre>
      </Drawer>
    </Layout>
  );
}
